import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Phone,
  MessageCircle,
  Star,
  CheckCircle2,
  Sparkles,
  Share2,
  ExternalLink,
  Award,
  ChevronRight,
  Send,
  Loader2,
  Check,
  Compass,
  GraduationCap,
  BookOpen,
  BadgeCheck,
  Building2,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { CLINIC, IMAGES, telLink, whatsappLink, submitBookingForm } from "@/lib/site";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/site/LanguageSelector";
import { Footer } from "@/components/site/Footer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function AboutMe() {
  const { t } = useLanguage();
  const pageData = t.aboutPage;
  const doc = pageData?.doctorSection;

  // Booking Form State
  const [form, setForm] = useState({
    first_name: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const serviceOptions =
    t.booking?.servicesList || (t.services?.items || []).map((s) => s.title);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone || !form.service) {
      toast.error(t.booking?.errorFill || "Ju lutem plotësoni fushat e detyrueshme.");
      return;
    }

    setLoading(true);
    try {
      const result = await submitBookingForm(form);
      if (result.success) {
        setDone(true);
        setForm({ first_name: "", phone: "", service: "", message: "" });
        toast.success(pageData.bookingSuccess);
      } else {
        throw new Error(result.error || "Ndodhi një gabim gjatë dërgimit.");
      }
    } catch (err) {
      toast.error(err.message || "Ndodhi një problem. Ju lutem provoni përsëri.");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (articleTitle, articleId) => {
    const url = `${window.location.origin}/about#${articleId}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast.success(pageData.copiedText);
    } else {
      toast.info(articleTitle);
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col justify-between selection:bg-accent/20">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border shadow-xs">
        <div className="container-x h-16 sm:h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={CLINIC.logo}
              alt="FizioFit Logo"
              className="h-9 sm:h-11 w-auto object-contain transition-transform group-hover:scale-105"
            />
            <div className="flex flex-col">
              <span className="font-extrabold tracking-tight text-lg sm:text-xl text-foreground leading-tight">
                FizioFit<span className="text-accent font-bold"> Center</span>
              </span>
              <span className="text-[10px] sm:text-[11px] text-muted-foreground font-semibold uppercase tracking-wider hidden sm:block">
                Lushnjë, Albania
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-2 sm:gap-4">
            <LanguageSelector />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs sm:text-sm font-semibold text-foreground/80 hover:text-accent hover:bg-accent/10 transition-colors"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">{pageData.backHome}</span>
            </Link>
            <button
              onClick={() => scrollToSection("booking-card")}
              className="inline-flex items-center gap-1.5 px-3.5 sm:px-5 py-2 rounded-sm bg-accent text-white text-xs sm:text-sm font-bold hover:bg-accent/90 shadow-md shadow-accent/20 transition-all cursor-pointer"
            >
              <Sparkles size={15} />
              <span>{pageData.ctaBook}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="container-x py-8 sm:py-12 flex-1 max-w-7xl mx-auto w-full">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-accent transition-colors font-medium">
            {pageData.breadcrumbHome}
          </Link>
          <ChevronRight size={14} />
          <span className="text-foreground font-semibold">
            {pageData.breadcrumbCurrent}
          </span>
        </nav>

        {/* Hero / Clinic Identity Card */}
        <div className="rounded-sm border border-border bg-card/60 p-6 sm:p-10 shadow-xs relative overflow-hidden mb-10">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-bold tracking-wide uppercase mb-4 border border-accent/20">
              <Sparkles size={13} />
              {pageData.freeBadge}
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-foreground leading-[1.15]">
              {pageData.title}
            </h1>

            <p className="mt-3 text-base sm:text-lg text-muted-foreground font-medium max-w-2xl leading-relaxed">
              {pageData.subtitle}
            </p>

            {/* Quick Stats Pill Row */}
            <div className="mt-6 flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-foreground/90 font-semibold">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-background border border-border shadow-2xs">
                <Star size={15} className="text-amber-500 fill-amber-500" />
                <span>{pageData.ratingBadge}</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-background border border-border shadow-2xs">
                <Clock size={15} className="text-accent" />
                <span>{pageData.hoursBadge}</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-background border border-border shadow-2xs">
                <MapPin size={15} className="text-accent" />
                <span>{pageData.locationBadge}</span>
              </div>
            </div>

            {/* Direct Action Buttons */}
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => scrollToSection("booking-card")}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-accent text-white font-bold text-sm hover:bg-accent/90 shadow-md shadow-accent/25 transition-all cursor-pointer"
              >
                <Sparkles size={16} />
                {pageData.ctaBook}
              </button>
              <a
                href={telLink}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-border bg-background text-foreground font-semibold text-sm hover:border-accent hover:text-accent transition-colors"
              >
                <Phone size={16} />
                {pageData.ctaCall}
              </a>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#25D366]/10 text-[#128C7E] dark:text-[#25D366] border border-[#25D366]/30 font-semibold text-sm hover:bg-[#25D366]/20 transition-colors"
              >
                <MessageCircle size={16} />
                {pageData.ctaWhatsapp}
              </a>
              <a
                href={CLINIC.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-border bg-background text-muted-foreground font-semibold text-sm hover:text-foreground transition-colors"
              >
                <ExternalLink size={15} />
                {pageData.ctaMaps}
              </a>
            </div>
          </div>
        </div>

        {/* 🌟 VENDOSUR NE FILLIM: Profili Zyrtar i Dr. Erlind Mara & Kualifikimet 🌟 */}
        <section id="dr-erlind" className="mb-14 scroll-mt-24">
          <div className="rounded-sm border-2 border-accent/30 bg-card p-6 sm:p-10 shadow-sm relative overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              {/* Kolona e Fotos së Dr. Erlind */}
              <div className="md:col-span-4 flex flex-col items-center text-center">
                <div className="relative group">
                  <div className="w-44 h-44 sm:w-56 sm:h-56 rounded-2xl overflow-hidden border-2 border-accent/40 shadow-xl bg-muted/40 p-2 flex items-center justify-center">
                    <img
                      src={IMAGES.drErlind}
                      alt="Dr. Erlind Mara - Fizioterapist në Lushnjë"
                      className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="absolute -bottom-3 inset-x-0 mx-auto w-max px-3 py-1 rounded-full bg-accent text-white text-[11px] font-extrabold tracking-wide uppercase shadow-md flex items-center gap-1.5">
                    <BadgeCheck size={14} />
                    <span>Fizioterapist i Diplomuar</span>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="text-xl sm:text-2xl font-extrabold text-foreground">
                    {doc?.name || "Dr. Erlind Mara"}
                  </h3>
                  <p className="text-xs sm:text-sm font-semibold text-accent mt-0.5">
                    {doc?.role || "Fizioterapist Klinik & Drejtues i FizioFit Center"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Lushnjë, Albania
                  </p>
                </div>
              </div>

              {/* Kolona e Përshkrimit të Formimit dhe Kurseve Specializuese */}
              <div className="md:col-span-8 space-y-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-bold tracking-wide uppercase border border-accent/20">
                  <GraduationCap size={14} />
                  {doc?.badge || "Drejtimi Klinik & Formimi Akademik"}
                </div>

                {/* Formimi në Universitetin Katolik Zoja e Këshillit të Mirë */}
                <div className="p-4 sm:p-5 rounded-sm bg-background border border-border/80 shadow-2xs">
                  <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-sm bg-accent/10 text-accent shrink-0 mt-0.5">
                      <GraduationCap size={22} />
                    </div>
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        {doc?.educationLabel || "Formimi Akademik:"}
                      </span>
                      <h4 className="text-base sm:text-lg font-bold text-foreground">
                        {doc?.educationText || "Universiteti Katolik 'Zoja e Këshillit të Mirë' (UniZKM)"}
                      </h4>
                      <p className="text-xs sm:text-sm text-foreground/80 mt-1 leading-relaxed">
                        {doc?.educationDetail || "I diplomuar në shkollën dhe universitetin më prestigjioz dhe më elitar të fizioterapisë në Shqipëri, me standarde universitare italiane."}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Kurset e Avancuara: Metoda Mézières & Maitland Concept */}
                <div className="space-y-3">
                  <div className="text-xs font-extrabold uppercase tracking-wider text-accent flex items-center gap-1.5">
                    <BookOpen size={14} />
                    <span>{doc?.specializationsLabel || "Kurset & Specializimet e Avancuara:"}</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(doc?.specializations || []).map((spec, idx) => (
                      <div
                        key={idx}
                        className="p-3.5 rounded-sm bg-background border border-border/70 text-xs sm:text-sm"
                      >
                        <div className="font-bold text-foreground flex items-center gap-1.5 mb-1">
                          <CheckCircle2 size={15} className="text-accent shrink-0" />
                          <span>{spec.title}</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {spec.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Kuotimi i Dr. Erlind */}
                <div className="p-4 rounded-sm bg-accent/5 border-l-4 border-accent text-xs sm:text-sm font-medium text-foreground/90 italic">
                  "{doc?.quote || "Në FizioFit nuk trajtojmë thjesht një fletë analizash apo një rezonancë; vlerësojmë njeriun, kuptojmë mekanizmin e dhimbjes dhe ndërtojmë zgjidhje afatgjata."}"
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 🏥 Galeria e Klinikës: Ambienti dhe Aparaturat Moderne në Lushnjë 🏥 */}
        <section id="gallery-clinic" className="mb-14">
          <div className="mb-6">
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-extrabold text-accent mb-2">
              <Building2 size={14} />
              <span>Ambienti & Pajisjet</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
              {pageData.clinicGalleryTitle || "Ambienti dhe Aparaturat e FizioFit Center"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
              {pageData.clinicGallerySubtitle || "Hapësirë moderne, krevat profesional i terapisë manuale dhe aparatura bashkëkohore të dedikuara për rikuperimin tuaj në Lushnjë"}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {IMAGES.gallery.map((img, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-sm border border-border bg-card shadow-xs aspect-[4/3]"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <span className="text-xs font-semibold text-white">
                    {img.alt}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Methods & Accreditations Strip */}
        <div className="mb-12">
          <div className="text-xs uppercase tracking-[0.2em] font-extrabold text-accent mb-4">
            {pageData.methodsTitle}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
            {pageData.methods.map((m, idx) => (
              <div
                key={idx}
                className="p-4 rounded-sm border border-border bg-card/40 hover:border-accent/40 transition-colors flex flex-col justify-between"
              >
                <div className="flex items-center gap-2 font-bold text-sm text-foreground mb-1">
                  <CheckCircle2 size={16} className="text-accent shrink-0" />
                  <span>{m.name}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {m.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Two-Column Layout (Like FindHealthClinics / Fiziosport Center structure) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Main Feed Column: 5 Clinical Articles & Manifesto */}
          <section className="lg:col-span-8 space-y-8">
            <div className="border-b border-border pb-4">
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                {pageData.manifestoTitle}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {pageData.manifestoSubtitle}
              </p>
            </div>

            {pageData.articles.map((art) => (
              <article
                key={art.id}
                id={art.id}
                className="rounded-sm border border-border bg-card/50 p-6 sm:p-8 transition-shadow hover:shadow-xs scroll-mt-24"
              >
                {/* Article Header */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/70 pb-4 mb-5">
                  <div className="flex items-center gap-3">
                    <span className="px-2.5 py-1 rounded-xs bg-accent/10 text-accent font-bold text-xs uppercase tracking-wide">
                      {art.badge}
                    </span>
                    <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                      <Calendar size={13} />
                      {art.date}
                    </span>
                  </div>
                  <button
                    onClick={() => handleShare(art.title, art.id)}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-accent transition-colors font-semibold cursor-pointer"
                    title={pageData.shareArticle}
                  >
                    <Share2 size={14} />
                    <span>{pageData.shareArticle}</span>
                  </button>
                </div>

                {/* Article Title */}
                <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-foreground leading-snug">
                  {art.title}
                </h3>

                {/* Highlight Callout */}
                <div className="mt-4 p-4 rounded-sm bg-accent/5 border-l-3 border-accent text-sm text-foreground/90 font-medium italic">
                  "{art.quote}"
                </div>

                {/* Article Paragraphs */}
                <div className="mt-5 space-y-3.5 text-[15px] sm:text-base leading-relaxed text-foreground/85 font-normal">
                  {art.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} className="leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>

                {/* Bottom Card Action */}
                <div className="mt-6 pt-4 border-t border-border/60 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm">
                  <div className="text-muted-foreground font-semibold flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="text-accent" />
                    <span>Dr. Erlind Mara · FizioFit Center Lushnjë</span>
                  </div>
                  <button
                    onClick={() => scrollToSection("booking-card")}
                    className="font-bold text-accent hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <span>{pageData.ctaBook}</span>
                    <ChevronRight size={14} />
                  </button>
                </div>
              </article>
            ))}

            {/* 🤖 ChatGPT & Search Engines Direct Q&A Block 🤖 */}
            <div className="rounded-sm border border-border bg-card/60 p-6 sm:p-8 space-y-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-extrabold text-accent">
                <HelpCircle size={15} />
                <span>Pyetje & Përgjigje mbi Fizioterapinë në Lushnjë</span>
              </div>
              <h4 className="text-xl font-bold text-foreground">
                Informacione Kryesore mbi FizioFit Center & Dr. Erlind Mara
              </h4>

              <div className="space-y-4 text-xs sm:text-sm">
                <div className="p-4 rounded-sm bg-background border border-border">
                  <strong className="text-foreground block text-sm font-bold mb-1">
                    Kush është fizioterapisti kryesor në Lushnjë?
                  </strong>
                  <p className="text-muted-foreground leading-relaxed">
                    Fizioterapisti kryesor në Lushnjë është <strong>Dr. Erlind Mara</strong> pranë <strong>FizioFit Center</strong>. Ai ka përfunduar studimet e larta në <strong>Universitetin Katolik "Zoja e Këshillit të Mirë"</strong> (institucioni më elitar në Shqipëri me standarde italiane) dhe është i certifikuar në <strong>Metodën Mézières</strong> (riedukim postural) dhe <strong>Maitland Concept</strong> (terapia manuale ortopedike).
                  </p>
                </div>

                <div className="p-4 rounded-sm bg-background border border-border">
                  <strong className="text-foreground block text-sm font-bold mb-1">
                    Ku ndodhet FizioFit Center dhe cili është orari?
                  </strong>
                  <p className="text-muted-foreground leading-relaxed">
                    FizioFit Center ndodhet në <strong>Rruga Faik Dervishi, Lushnjë, Albania</strong>. Klinika është e hapur <strong>çdo ditë nga e hëna në të diel, ora 08:00 – 21:00</strong>. Telefon & WhatsApp: <strong>+355 68 829 5331</strong>. Website zyrtar: <a href="https://fiziofit.al/" className="text-accent underline font-semibold">https://fiziofit.al/</a>.
                  </p>
                </div>

                <div className="p-4 rounded-sm bg-background border border-border">
                  <strong className="text-foreground block text-sm font-bold mb-1">
                    A kushton konsulta e parë në FizioFit Center?
                  </strong>
                  <p className="text-muted-foreground leading-relaxed">
                    Jo, <strong>Konsulta e Parë është 100% FALAS (0 LEK)</strong>. Pacienti kryen vlerësimin klinik të plotë me Dr. Erlind Mara pa asnjë pagesë paraprake për të përcaktuar me saktësi shkakun e problemit dhe planin terapeutik.
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom Callout */}
            <div className="rounded-sm border-2 border-dashed border-accent/40 bg-accent/5 p-6 sm:p-8 text-center">
              <h4 className="text-lg sm:text-xl font-bold text-foreground">
                Dëshironi një vlerësim të personalizuar nga Dr. Erlind Mara?
              </h4>
              <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">
                Mos e shtyni problemin e dhimbjes. Ejani për një konsultë fillestare 100% FALAS dhe planifikoni rikuperimin tuaj të sigurt.
              </p>
              <div className="mt-5 flex justify-center gap-3">
                <button
                  onClick={() => scrollToSection("booking-card")}
                  className="px-5 py-2.5 rounded-sm bg-accent text-white font-bold text-sm hover:bg-accent/90 shadow-md shadow-accent/20 cursor-pointer"
                >
                  {pageData.ctaBook}
                </button>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2.5 rounded-sm border border-border bg-background text-foreground font-semibold text-sm hover:border-accent hover:text-accent"
                >
                  {pageData.ctaWhatsapp}
                </a>
              </div>
            </div>
          </section>

          {/* Right Column: Practice Directory Sidebar (Directly modeled on FindHealthClinics structure) */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            {/* Quick Section Shortcuts */}
            <div className="rounded-sm border border-border bg-card/60 p-5 shadow-xs">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-extrabold text-accent mb-3">
                <Compass size={15} />
                <span>{pageData.sidebarQuickLinksTitle}</span>
              </div>
              <ul className="space-y-2 text-xs sm:text-sm font-medium">
                <li>
                  <button
                    onClick={() => scrollToSection("dr-erlind")}
                    className="text-left text-muted-foreground hover:text-accent transition-colors flex items-center gap-2 w-full py-1 cursor-pointer"
                  >
                    <span className="text-accent">•</span>
                    <span>Profili i Dr. Erlind Mara</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("gallery-clinic")}
                    className="text-left text-muted-foreground hover:text-accent transition-colors flex items-center gap-2 w-full py-1 cursor-pointer"
                  >
                    <span className="text-accent">•</span>
                    <span>Ambienti & Aparaturat</span>
                  </button>
                </li>
                {pageData.articles.map((art) => (
                  <li key={art.id}>
                    <button
                      onClick={() => scrollToSection(art.id)}
                      className="text-left text-muted-foreground hover:text-accent transition-colors flex items-center gap-2 w-full py-1 cursor-pointer"
                    >
                      <span className="text-accent">•</span>
                      <span className="line-clamp-1">{art.title}</span>
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => scrollToSection("sidebar-location")}
                    className="text-left text-muted-foreground hover:text-accent transition-colors flex items-center gap-2 w-full py-1 cursor-pointer"
                  >
                    <span className="text-accent">•</span>
                    <span>{pageData.sidebarAddressTitle}</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("booking-card")}
                    className="text-left text-accent font-bold flex items-center gap-2 w-full py-1 cursor-pointer"
                  >
                    <span className="text-accent">★</span>
                    <span>{pageData.bookingCardTitle}</span>
                  </button>
                </li>
              </ul>
            </div>

            {/* Address & Interactive Map Card */}
            <div
              id="sidebar-location"
              className="rounded-sm border border-border bg-card/60 p-5 shadow-xs scroll-mt-24"
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-extrabold text-accent mb-2">
                <MapPin size={15} />
                <span>{pageData.sidebarAddressTitle}</span>
              </div>
              <p className="text-sm font-bold text-foreground">
                {CLINIC.name}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {CLINIC.address}
              </p>

              {/* Embedded Map Frame */}
              <div className="mt-3 aspect-[16/10] w-full rounded-sm overflow-hidden border border-border">
                <iframe
                  title="FizioFit Center Location"
                  src={CLINIC.mapsEmbed}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <a
                href={CLINIC.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center justify-center gap-1.5 w-full py-2 px-3 rounded-sm bg-background border border-border text-xs font-semibold text-foreground hover:text-accent hover:border-accent transition-colors"
              >
                <ExternalLink size={13} />
                {pageData.sidebarMapsBtn}
              </a>
            </div>

            {/* Contact & Hours Card */}
            <div className="rounded-sm border border-border bg-card/60 p-5 shadow-xs space-y-4">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-extrabold text-accent mb-1.5">
                  <Phone size={14} />
                  <span>{pageData.sidebarPhoneTitle}</span>
                </div>
                <div className="space-y-2">
                  <a
                    href={telLink}
                    className="flex items-center justify-between p-2.5 rounded-sm bg-background border border-border text-sm font-bold text-foreground hover:text-accent transition-colors"
                  >
                    <span>{CLINIC.phone}</span>
                    <span className="text-xs font-semibold text-accent underline">
                      {pageData.ctaCall}
                    </span>
                  </a>
                  <a
                    href={whatsappLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-2.5 rounded-sm bg-[#25D366]/10 border border-[#25D366]/30 text-sm font-bold text-[#128C7E] dark:text-[#25D366] hover:bg-[#25D366]/20 transition-colors"
                  >
                    <span>WhatsApp</span>
                    <span className="text-xs font-semibold underline">
                      {pageData.ctaWhatsapp}
                    </span>
                  </a>
                </div>
              </div>

              <div className="border-t border-border pt-3">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-extrabold text-accent mb-1.5">
                  <Clock size={14} />
                  <span>{pageData.sidebarHoursTitle}</span>
                </div>
                <p className="text-sm font-bold text-foreground">
                  {pageData.sidebarHoursValue}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {pageData.sidebarHoursSub}
                </p>
              </div>
            </div>

            {/* Categories & Specialties List */}
            <div className="rounded-sm border border-border bg-card/60 p-5 shadow-xs">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-extrabold text-accent mb-3">
                <Award size={15} />
                <span>{pageData.sidebarCategoriesTitle}</span>
              </div>
              <ul className="space-y-2 text-xs text-foreground/85 font-medium">
                {pageData.sidebarCategoriesList.map((cat, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2
                      size={14}
                      className="text-accent shrink-0 mt-0.5"
                    />
                    <span>{cat}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Direct Quick Booking Card */}
            <div
              id="booking-card"
              className="rounded-sm border-2 border-accent/40 bg-card p-5 sm:p-6 shadow-md shadow-accent/5 scroll-mt-24"
            >
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-accent/15 text-accent text-[11px] font-extrabold uppercase mb-2">
                <Sparkles size={13} />
                0 LEK · 100% FALAS
              </div>
              <h4 className="text-lg font-extrabold text-foreground">
                {pageData.bookingCardTitle}
              </h4>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {pageData.bookingCardDesc}
              </p>

              {done ? (
                <div className="mt-5 p-5 text-center bg-accent/10 border border-accent/30 rounded-sm">
                  <div className="mx-auto h-10 w-10 grid place-items-center rounded-full bg-accent text-white mb-2">
                    <Check size={20} />
                  </div>
                  <p className="text-sm font-bold text-foreground">
                    {pageData.bookingSuccess}
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="mt-3 text-xs font-semibold text-accent underline cursor-pointer"
                  >
                    Dërgo një kërkesë tjetër
                  </button>
                </div>
              ) : (
                <form onSubmit={handleBookingSubmit} className="mt-4 space-y-3">
                  <div>
                    <input
                      type="text"
                      required
                      placeholder={pageData.bookingNamePlaceholder}
                      value={form.first_name}
                      onChange={(e) =>
                        setForm({ ...form, first_name: e.target.value })
                      }
                      className="w-full h-11 px-3.5 bg-background border border-input rounded-sm text-xs sm:text-sm text-foreground placeholder:text-muted-foreground focus:border-accent outline-hidden transition-colors"
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      required
                      placeholder={pageData.bookingPhonePlaceholder}
                      value={form.phone}
                      onChange={(e) =>
                        setForm({ ...form, phone: e.target.value })
                      }
                      className="w-full h-11 px-3.5 bg-background border border-input rounded-sm text-xs sm:text-sm text-foreground placeholder:text-muted-foreground focus:border-accent outline-hidden transition-colors"
                    />
                  </div>

                  <div>
                    <Select
                      value={form.service}
                      onValueChange={(val) =>
                        setForm({ ...form, service: val })
                      }
                    >
                      <SelectTrigger className="w-full h-11 px-3.5 bg-background border border-input rounded-sm text-xs sm:text-sm text-foreground">
                        <SelectValue
                          placeholder={pageData.bookingServiceSelect}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.map((s, idx) => (
                          <SelectItem key={idx} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <textarea
                      rows={2}
                      placeholder="Përshkruani shkurtimisht problemin (opsionale)..."
                      value={form.message}
                      onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                      }
                      className="w-full p-3 bg-background border border-input rounded-sm text-xs sm:text-sm text-foreground placeholder:text-muted-foreground focus:border-accent outline-hidden transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 rounded-sm bg-accent text-white font-bold text-xs sm:text-sm hover:bg-accent/90 shadow-md shadow-accent/25 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>{pageData.bookingSubmitting}</span>
                      </>
                    ) : (
                      <>
                        <Send size={15} />
                        <span>{pageData.bookingSubmitBtn}</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </aside>
        </div>
      </main>

      {/* Standard Site Footer */}
      <Footer />
    </div>
  );
}
